/**
 * FP3-092 — ONE shared student-profile taxonomy for every surface
 * (Admin Profile tab, Provider Service Demographics, add/import
 * flows). Admin and Provider may differ in edit permissions but must
 * never differ in value schemas.
 *
 * Canonical persisted keys mirror backend `student_profile.py`:
 *   attendance_pattern : full_day | am | pm | partial | ""
 *   sex                : female | male | other | ""
 *   status             : active(IEP) | rti | monitoring | graduated
 *                        (+ legacy readable: evaluation, exited)
 *   lre                : /api/lre-levels `key` property (A…J)
 */

export const ATTENDANCE_OPTIONS = [
  { value: "full_day", label: "Full day" },
  { value: "am", label: "Half day · AM" },
  { value: "pm", label: "Half day · PM" },
  { value: "partial", label: "Partial / variable" },
];

const ATTENDANCE_ALIASES = {
  full_day: "full_day",
  "full day": "full_day",
  fullday: "full_day",
  full: "full_day",
  am: "am",
  half_day_am: "am",
  "half day am": "am",
  "half-day-am": "am",
  halfday_am: "am",
  pm: "pm",
  half_day_pm: "pm",
  "half day pm": "pm",
  "half-day-pm": "pm",
  halfday_pm: "pm",
  partial: "partial",
  partial_variable: "partial",
  "partial / variable": "partial",
  variable: "partial",
};

export function normalizeAttendancePattern(value) {
  const raw = (value == null ? "" : String(value)).trim().toLowerCase();
  if (!raw) return "";
  return ATTENDANCE_ALIASES[raw] || raw;
}

export function attendanceLabel(value) {
  const key = normalizeAttendancePattern(value);
  const opt = ATTENDANCE_OPTIONS.find((o) => o.value === key);
  return opt ? opt.label : (value || "—");
}

export const SEX_OPTIONS = [
  { value: "", label: "Not specified" },
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

const SEX_ALIASES = {
  f: "female",
  female: "female",
  m: "male",
  male: "male",
  nb: "other",
  x: "other",
  nonbinary: "other",
  "non-binary": "other",
  "non binary": "other",
  other: "other",
};

export function normalizeSex(value) {
  const raw = (value == null ? "" : String(value)).trim().toLowerCase();
  if (!raw) return "";
  return SEX_ALIASES[raw] || raw;
}

export function sexLabel(value) {
  const key = normalizeSex(value);
  const opt = SEX_OPTIONS.find((o) => o.value === key);
  return opt ? opt.label : (value || "—");
}

// Service status — Archive is the separate `is_archived` flag and is
// intentionally NOT part of this list.
export const SERVICE_STATUS_OPTIONS = [
  { value: "active", label: "IEP" },
  { value: "rti", label: "RTI" },
  { value: "monitoring", label: "Monitoring" },
  { value: "graduated", label: "Graduated" },
];

export const LEGACY_STATUS_LABELS = {
  evaluation: "Evaluation only (legacy)",
  exited: "Exited (legacy)",
};

export function serviceStatusLabel(value) {
  const raw = (value == null ? "" : String(value)).trim().toLowerCase();
  const opt = SERVICE_STATUS_OPTIONS.find((o) => o.value === raw);
  if (opt) return opt.label;
  return LEGACY_STATUS_LABELS[raw] || (value || "—");
}

// LRE — both surfaces bind to the `key` property of /api/lre-levels
// rows ({key, label}); never `code`.
export const LRE_KEY_PROP = "key";
